import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import useAuth from '@/common/composable/useAuth'

// classes
import { CatchedError } from './CatchedError.class'

// headers constants
const JSON_HEADER = 'application/json'
const FORMDATA_HEADER = 'multipart/form-data'

// constants
import { HTTP_ERROR_MESSAGE } from '@/core/constants/http.constants'

export class Http {
    private _provider: AxiosInstance

    constructor(baseURL: string) {
        this._provider = axios
        this._provider.defaults.baseURL = baseURL

        this._provider.interceptors.response.use(
            (response: AxiosResponse) => {
                return Promise.resolve(response)
            },
            (error: AxiosError) => {
                const response = error.response
                const message = response?.data.message

                if (axios.isCancel(error)) {
                    const catched_error = new CatchedError({ is_cancel: true })
                    return Promise.reject(catched_error)
                }

                if (response) {
                    const status = response.status
                    const url = response.config.url

                    // if user token is expired
                    if (status === 401) {
                        window.location.href = '/auth'

                        const { removeAuthorization } = useAuth()
                        removeAuthorization()
                    }

                    if (status === 418 || status === 422) {
                        let parsed_errors = [] as string[]

                        if (response.data.errors) {
                            const validation_errors = Object.values(
                                response.data.errors
                            ) as string[][]
                            validation_errors.forEach((validation_error_array: string[]) => {
                                parsed_errors = parsed_errors.concat(validation_error_array)
                            })
                        }

                        if (response.data.message) {
                            parsed_errors.push(response.data.message)
                        }

                        return Promise.reject(parsed_errors)
                    }

                    const catched_error = new CatchedError({ message, status, url })
                    return Promise.reject(catched_error)
                }

                const catched_error = new CatchedError({
                    message: HTTP_ERROR_MESSAGE,
                })
                return Promise.reject(catched_error)
            }
        )
    }

    setAuthorization(token: string, token_type: string): void {
        this._provider.defaults.headers.common.Authorization = `${token_type} ${token}`
    }

    removeAuthorization(): void {
        this._provider.defaults.headers.common.Authorization = ''
    }

    token(): string {
        return this._provider.defaults.headers.common.Authorization
    }

    get(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        if (config && config.headers) {
            config.headers['Content-Type'] = JSON_HEADER
        }

        return this._provider.get(url, config)
    }

    post(url: string, data: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        if (config && config.headers) {
            config.headers['Content-Type'] = JSON_HEADER
        }

        return this._provider.post(url, data, config)
    }

    put(url: string, data: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        if (config && config.headers) {
            config.headers['Content-Type'] = JSON_HEADER
        }

        return this._provider.put(url, data, config)
    }

    delete(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        if (config && config.headers) {
            config.headers['Content-Type'] = JSON_HEADER
        }

        return this._provider.delete(url, config)
    }

    postFormData(
        url: string,
        data: FormData | undefined,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        if (config && config.headers) {
            config.headers['Content-Type'] = FORMDATA_HEADER
        }

        return this._provider.post(url, data, config)
    }

    putFormData(
        url: string,
        data: FormData | undefined,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        if (config && config.headers) {
            config.headers['Content-Type'] = FORMDATA_HEADER
        }

        return this._provider.put(url, data, config)
    }

    get provider(): AxiosInstance {
        return this._provider
    }

    get authorizationHeader(): string {
        return this._provider.defaults.headers.common.Authorization
    }
}
